import { GET_LAWYERS, userService } from '@/api';
import { Button, Typography, UserBadge } from '@/components/common';
import { ListLawyersComponent } from '@/components/modals';
import { CaseContext } from '@/context/CaseContext';
import { useUser } from '@/hooks';
import useModal from '@/hooks/useModal';
import { InfoCircle } from '@/public/icons/tsx';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { rules } from './rules';
import { CaseDetailsLawyer, LawyerItem } from '@/interfaces';

interface CaseTeamOfLawyersProps {
	applyRule: boolean;
	caseDetails?: CaseDetailsLawyer;
	isPublished?: boolean;
}
const CaseTeamOfLawyers: React.FC<CaseTeamOfLawyersProps> = ({
	applyRule,
	caseDetails,
	isPublished,
}) => {
	const { Modal, show, hide } = useModal();
	const { user } = useUser();
	const [lawyersSelected, setLawyersSelected] = React.useState<LawyerItem[]>(
		[]
	);
	const { isCaseReadOnly } = useContext(CaseContext);
	const isDisabled = isCaseReadOnly || isPublished;
	const {
		register,
		setValue,
		formState: { errors },
		trigger,
	} = useFormContext();

	// Lawyers
	const { data: lawyers } = useQuery({
		queryKey: [GET_LAWYERS],
		queryFn: () => userService.getLawyers(),
	});

	React.useEffect(() => {
		// Normalize caseDetails.lawyers (Lawyer[]) → LawyerItem[]

		const initialLawyers: LawyerItem[] = (caseDetails?.lawyers || []).map(
			(lawyer) => ({
				id: lawyer.id,
				firstname: lawyer.username.split(' ')[0] || '', // mapping username to firstname
				lastname: lawyer.username.split(' ')[1] || '', // no lastname info available
				profile_picture: lawyer.profile_picture,
				is_own: lawyer.is_own,
				username: lawyer.username,
			})
		);

		// Check if the logged-in user is a lawyer and not already in the list
		const isLawyer = user && user.role === 'lawyer';
		const alreadyIncluded = initialLawyers.some((l) => l.id === user?.id);

		const updatedLawyers = [...initialLawyers];

		if (isLawyer && !alreadyIncluded) {
			updatedLawyers.push({
				id: user.id,
				firstname: user.firstname || '',
				lastname: user.lastname || '',
				profile_picture: user.profile_picture || '',
				is_own: true,
			});
		}

		setLawyersSelected(updatedLawyers);
	}, [caseDetails, user]);

	const handleSelectLawyers = (lawyers: LawyerItem[]) => {
		setLawyersSelected(lawyers);

		// Extract just the IDs for the form field
		const ids = lawyers.map((lawyer) => lawyer.id);

		setValue('lawyerIds', ids, {
			shouldDirty: true,
		});
		trigger('lawyerIds');

		hide();
	};
	const handleRemoveLawyer = (idToRemove: string) => {
		const newList = lawyersSelected.filter(
			(lawyer) => lawyer.id !== idToRemove
		);

		if (newList.length !== lawyersSelected.length) {
			const ids = newList.map((lawyer) => lawyer.id);
			setValue('lawyerIds', ids, {
				shouldDirty: true,
			});
			setLawyersSelected(newList);
		} else {
			trigger('lawyerIds');
		}
	};

	return (
		<>
			<div className="!mt-8 space-y-6">
				<div>
					<Typography type="semibold-20" className="!text-[20px]">
						Team of Lawyers
					</Typography>
				</div>
				<div className="space-y-6">
					<div>
						<input
							readOnly
							placeholder="Select"
							className={'hidden'}
							{...register(
								'lawyerIds',
								applyRule ? rules.lawyerIds : undefined
							)}
						/>
						<Button
							label="Select the team of lawyers"
							decoration="secondary"
							size="fit"
							onClick={show}
							// disabled={isDisabled}
						/>
						{errors?.lawyerIds && applyRule && (
							<div className="flex items-center gap-x-1.5">
								<InfoCircle fill="#FF0043" width="16" height="16" />
								<span className="flex items-center mt-1 gap-1">
									<Typography type="regular-12" className="text-main-red">
										{errors?.lawyerIds.message as string}
									</Typography>
								</span>
							</div>
						)}
					</div>
					<div className="space-y-4">
						<div className="border border-[#0D061633] rounded-2xl p-6 flex flex-wrap items-center gap-6  h-auto">
							{lawyersSelected.length > 0 &&
								lawyersSelected
									.sort((a, b) => (b.is_own ? 1 : 0) - (a.is_own ? 1 : 0))
									.map((lawyer) => (
										<UserBadge
											key={lawyer.id}
											firstname={lawyer.firstname || ''}
											lastname={lawyer.lastname || ''}
											username={lawyer.username || ''}
											isCreator={lawyer.is_own}
											photoUrl={lawyer.profile_picture || ''}
											onClick={() => handleRemoveLawyer(lawyer.id)}
											className={clsx({
												'hover:!bg-[#F4F4F4A3]': isDisabled,
												'!justify-start gap-2 border-primary': lawyer.is_own,
											})}
										/>
									))}
						</div>
					</div>
				</div>
			</div>{' '}
			<Modal
				withXButton
				title="Select the team of lawyers"
				classNameTitle="!text-base md:!text-xl mb-16 md:mb-16 text-left ml-4"
				classNameCardContainer="h-[564px] min-w-[672px]"
				classNameContainerChildren={'!mb-16 !max-h-[38vh] 2xl:!max-h-[35vh]'}
			>
				<ListLawyersComponent
					listLawyers={lawyers?.data.items || []}
					currentLawyersSelected={lawyersSelected}
					handleSelection={handleSelectLawyers}
				/>
			</Modal>
		</>
	);
};

export default CaseTeamOfLawyers;
